import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, GuildDetailResponse, UpdateGuildPayload } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface UseGuildReturn {
  guild: GuildDetailResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  update: (input: UpdateGuildPayload) => Promise<void>;
  remove: () => Promise<void>;
  join: () => Promise<void>;
  leave: () => Promise<void>;
}

export function useGuild(guildId: string | undefined): UseGuildReturn {
  const queryClient = useQueryClient();
  const guildQueryKey = queryKeys.guilds.detail(guildId!);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: guildQueryKey,
    queryFn: () => api.getGuildDetail(guildId!),
    enabled: !!guildId,
  });

  const updateMut = useMutation({
    mutationFn: (input: UpdateGuildPayload) => api.updateGuild(guildId!, input),
    onSuccess: (result) => {
      queryClient.setQueryData(guildQueryKey, (prev: GuildDetailResponse | undefined) =>
        prev ? { ...prev, ...result, myRole: result.myRole ?? prev.myRole } : result,
      );
    },
  });

  const removeMut = useMutation({
    mutationFn: () => api.deleteGuild(guildId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.guilds.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.guilds.my() });
    },
  });

  const joinMut = useMutation({
    mutationFn: () => api.joinGuild(guildId!),
    onSuccess: () => {
      refetch();
      queryClient.invalidateQueries({ queryKey: queryKeys.guilds.my() });
      // Membership changes what the caller may do here — a cached permission
      // set from before the join/leave would gate the UI wrongly.
      queryClient.invalidateQueries({ queryKey: queryKeys.guilds.permissions(guildId ?? '') });
    },
  });

  const leaveMut = useMutation({
    mutationFn: () => api.leaveGuild(guildId!),
    onSuccess: () => {
      refetch();
      queryClient.invalidateQueries({ queryKey: queryKeys.guilds.my() });
      queryClient.invalidateQueries({ queryKey: queryKeys.guilds.permissions(guildId ?? '') });
    },
  });

  return {
    guild: data ?? null,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load guild' : null,
    refresh: () => {
      refetch();
    },
    update: async (input) => {
      await updateMut.mutateAsync(input);
    },
    remove: async () => {
      if (!guildId) return;
      await removeMut.mutateAsync();
    },
    join: async () => {
      if (!guildId) return;
      await joinMut.mutateAsync();
    },
    leave: async () => {
      if (!guildId) return;
      await leaveMut.mutateAsync();
    },
  };
}
